/**
 * Question bank facade.
 *
 * Default exports resolve to the CURRENT bank (v2). Both v1 (archived) and
 * v2 are also re-exported explicitly so consumers can opt into a specific
 * version. Use `getQuestionBank(scoreVersion)` to select at runtime.
 *
 * Layout (Sprint 6 / ADR-014):
 *   - question-bank-v1.ts  archived, frozen v1 bank (`_V1` suffixed exports)
 *   - question-bank-v2.ts  current v2 bank (20 q, planning + growth added)
 *   - question-bank.ts      this facade — defaults to v2
 *
 * Source of truth: docs/02-product/question-bank-v2.md (v2, current),
 * docs/02-product/question-bank.md (v1, archived).
 */

// Default surface = v2 (current). Imported via `@/lib/quiz/question-bank`.
export {
  QUESTION_BANK_V2 as QUESTION_BANK,
  QUESTION_BY_ID_V2 as QUESTION_BY_ID,
  TOTAL_QUESTIONS_V2 as TOTAL_QUESTIONS,
  DIMENSIONS,
  DIMENSION_TO_ARCHETYPE,
  BANK_VERSION_V2 as BANK_VERSION,
  SCORE_VERSION_V2 as SCORE_VERSION,
  SCORE_VERSION_INT_V2 as SCORE_VERSION_INT,
  CONTENT_VERSION_V2 as CONTENT_VERSION,
} from "./question-bank-v2";

// The Question shape and dimension types live in v2 (the current bank).
export type {
  Question,
  Dimension,
  QuestionType,
  AnswerOption,
} from "./question-bank-v2";

// Explicit v1 surface (archived, opt-in).
export {
  QUESTION_BANK_V1,
  QUESTION_BY_ID_V1,
  TOTAL_QUESTIONS_V1,
  DIMENSIONS_V1,
  DIMENSION_TO_ARCHETYPE_V1,
  BANK_VERSION_V1,
  SCORE_VERSION_V1,
  SCORE_VERSION_INT_V1,
  CONTENT_VERSION_V1,
} from "./question-bank-v1";
export type {
  QuestionV1,
  DimensionV1,
  QuestionTypeV1,
  AnswerOptionV1,
} from "./question-bank-v1";

// Explicit v2 surface (current, opt-in).
export {
  QUESTION_BANK_V2,
  QUESTION_BY_ID_V2,
  TOTAL_QUESTIONS_V2,
  BANK_VERSION_V2,
  SCORE_VERSION_V2,
  SCORE_VERSION_INT_V2,
  CONTENT_VERSION_V2,
} from "./question-bank-v2";

import { QUESTION_BANK_V1, type QuestionV1 } from "./question-bank-v1";
import { QUESTION_BANK_V2, type Question } from "./question-bank-v2";

/**
 * Select a question bank by score version. Accepts either the integer form
 * (1 | 2) or the string form ("v1.0.0" | "v2.0.0"). Throws on anything else.
 */
export function getQuestionBank(
  scoreVersion: 1 | 2 | "v1.0.0" | "v2.0.0",
): ReadonlyArray<Question> | ReadonlyArray<QuestionV1> {
  switch (scoreVersion) {
    case 1:
    case "v1.0.0":
      return QUESTION_BANK_V1;
    case 2:
    case "v2.0.0":
      return QUESTION_BANK_V2;
    default:
      throw new Error(`getQuestionBank: unknown score version "${scoreVersion}".`);
  }
}
